import { useContext, useMemo } from 'react';
import { BasketContext } from '../providers/basketContext';
import { ForceMaximumRequirementsLength, QuoteRequirements } from '~/lib/types/basket-types';
import { RequirementTypes } from '~/lib/types/enums';

/**
 * A hook used to read the requirements to complete the current checkout. e.g. Field names, validation rules and if the field is required.
 */
export default function useQuoteRequirements() {
  const { state } = useContext(BasketContext);

  const loading = useMemo(() => {
    return !state.requirements || Object.keys(state.requirements).length === 0;
  }, [state.requirements]);

  const requirements: QuoteRequirements = useMemo(() => {
    if (loading) return {};
    const requirements = state.requirements ?? {};
    return Object.getOwnPropertyNames(requirements).reduce((acc: QuoteRequirements, x) => {
      if (x === RequirementTypes.ORIGIN || x === RequirementTypes.DESTINATION) {
        const address = requirements[x];
        for (const rule of address.rules) {
          if (!rule.metadata.maxLength && ForceMaximumRequirementsLength[rule.key] !== undefined) {
            rule.metadata.maxLength = ForceMaximumRequirementsLength[rule.key];
          }
        }
        acc[x] = address;
      } else {
        acc[x] = requirements[x];
      }
      return acc;
    }, {});
  }, [loading, state.requirements]);

  const countries = useMemo(() => {
    return {
      origin: state.origins ?? [],
      destination: state.destinations ?? []
    };
  }, [state.origins, state.destinations]);

  const requiresCustoms: boolean = useMemo(() => {
    return (
      requirements[RequirementTypes.CUSTOMS] !== undefined ||
      requirements[RequirementTypes.ORIGIN]?.metadata?.['CustomsType'] === 'WINDSOR'
    );
  }, [requirements]);

  return {
    /** The requirements to complete the current checkout */
    requirements,
    /** The countries that are available for shipping */
    countries,
    /** If the current checkout requires customs information */
    requiresCustoms,
    /** If the requirements are still being fetched */
    loading
  };
}
